import { Disclosure, Transition } from '@headlessui/react';
import classNames from 'classnames';
import { TaskResult, TaskWithRelationsResponse } from 'compliance-api/generated';
import dayjs from 'dayjs';
import { useCallback, useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { commonPaths } from '@/modules/common/routes.tsx';
import UsersAvatarsSet from '@/modules/admin-dashboard/components/UsersAvatarsSet.tsx';
import TaskResultInfoModal from '@/modules/admin-dashboard/components/modals/TaskResultInfoModal.tsx';
import { useCurrentUserQuery } from '@/modules/auth/queries/user.tsx';
import { Avatar, ModalProviderContext } from 'ui';
import { ChevronDownIcon } from 'ui/icons';

interface LinkedTaskItemProps {
  task: TaskWithRelationsResponse;
}

const LinkedTaskItem = ({ task }: LinkedTaskItemProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const { name, id, overdue_date, users = [], task_results = [] } = task;
  const { data: currentUserData } = useCurrentUserQuery();
  const { openModal } = useContext(ModalProviderContext);

  const currentUserId = currentUserData?.user?.id || 0;

  const handleToggleOpen = useCallback(() => {
    setOpen(open => !open);
  }, []);

  return (
    <div key={id}>
      <div
        className={classNames('flex gap-2 justify-between', { 'cursor-pointer': users.length })}
        onClick={users.length ? handleToggleOpen : undefined}
      >
        <div className="flex-1">
          <span className="text-sm">{name}</span>
          <div className="text-xs text-neutral-500">
            {dayjs(overdue_date).format('DD-MM-YYYY, HH:mm')}
          </div>
        </div>
        <UsersAvatarsSet users={users} />
        <img
          src={ChevronDownIcon}
          alt="open"
          className={classNames({ 'rotate-180': open, 'opacity-0': !users.length })}
        />
      </div>
      <Disclosure>
        <Transition
          show={open && !!users.length}
          enter="transition duration-100 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100"
          leave="transition duration-75 ease-out"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0"
        >
          <Disclosure.Panel static>
            <div className="flex flex-col mt-4">
              {users.map(user => {
                const { first_name, id, last_name, avatar_url } = user;
                const taskResult = task_results.find(
                  ({ completed_by_user_id }) => completed_by_user_id === id,
                );
                const isTaskCompleted = taskResult?.result === TaskResult.Completed;
                const isMyTask = id === currentUserId;

                return (
                  <div
                    className="grid grid-cols-[auto_1fr_auto] gap-3 border-t items-center py-2"
                    key={id}
                  >
                    <Avatar src={avatar_url} firstName={first_name} lastName={last_name} />

                    {!isTaskCompleted && isMyTask && (
                      <Link
                        to={{
                          pathname: commonPaths.dynamic.task(task.id),
                          search: location.search,
                        }}
                        className="hover:underline"
                      >
                        {first_name} {last_name}
                      </Link>
                    )}
                    {!isTaskCompleted && !isMyTask && (
                      <span>
                        {first_name} {last_name}
                      </span>
                    )}
                    {isTaskCompleted && (
                      <span
                        className="hover:underline cursor-pointer"
                        onClick={
                          taskResult
                            ? () =>
                                openModal(TaskResultInfoModal, {
                                  taskResultInfo: { task, user, result: taskResult },
                                })
                            : undefined
                        }
                      >
                        {first_name} {last_name}
                      </span>
                    )}

                    <span className="capitalize">
                      {task_results.find(({ completed_by_user_id }) => completed_by_user_id === id)
                        ?.result || '---'}
                    </span>
                  </div>
                );
              })}
            </div>
          </Disclosure.Panel>
        </Transition>
      </Disclosure>
    </div>
  );
};

export default LinkedTaskItem;
